import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { api, brl } from "@/lib/api";
import { METHODS } from "@/components/PaymentDialog";

const today = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const sel = "h-11 w-full rounded-md bg-zinc-950 border border-zinc-800 text-zinc-100 px-3 text-sm";
const MACHINE_METHODS = new Set(["debito", "pix", "credito"]);

export function PaymentEntryDialog({ entry, suggested, onClose, onSave }) {
  const [machines, setMachines] = useState([]);
  useEffect(() => { api.get("/shop-settings/payment-machines").then(({ data }) => setMachines((data || []).filter((m) => m.active))).catch(() => {}); }, []);
  const defaultId = useMemo(() => (machines.find((m) => m.is_default) || machines[0])?.id || "", [machines]);
  const [f, setF] = useState(() => ({
    method: entry?.method || "pix",
    amount: entry ? String(entry.amount) : suggested > 0 ? String(suggested) : "",
    date: entry?.date || today(),
    paid: entry ? entry.paid : true,
    card: String(entry?.card_installments || 1),
    split: "1",
    note: entry?.note || "",
    machine_id: entry?.machine_id || "",
    auth_code: entry?.auth_code || "",
  }));
  useEffect(() => {
    setF((p) => (MACHINE_METHODS.has(p.method) && !p.machine_id && defaultId) ? { ...p, machine_id: defaultId } : p);
  }, [defaultId]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => {
    const next = { ...p, [k]: v };
    if (k === "method") next.machine_id = MACHINE_METHODS.has(v) ? (p.machine_id || defaultId) : "";
    return next;
  });
  const amount = parseFloat(String(f.amount).replace(",", ".")) || 0;
  const split = Math.max(1, Math.min(24, parseInt(f.split) || 1));
  const m = f.machine_id ? machines.find((x) => x.id === f.machine_id) : null;
  let rate = 0;
  if (m) {
    rate = f.method === "debito" ? Number(m.debito || 0) : f.method === "pix" ? Number(m.pix || 0) : f.method === "credito" ? Number(m.credito?.[f.card] || 0) : 0;
    if (f.method === "credito" && m.advance_enabled) rate += Number(m.advance_rate || 0);
  }
  const fee = +((amount * rate) / 100).toFixed(2);
  const submit = async () => {
    setSaving(true);
    try {
      await onSave({
        method: f.method, amount, date: f.date,
        paid: f.method === "dinheiro" ? true : (split === 1 && f.paid),
        card_installments: f.method === "credito" ? parseInt(f.card) : null,
        note: f.note.trim() || null, split: entry ? null : split,
        machine_id: MACHINE_METHODS.has(f.method) ? (f.machine_id || null) : null,
        auth_code: (f.method === "credito" || f.method === "debito") ? (f.auth_code.trim() || null) : null,
      });
    } finally { setSaving(false); }
  };
  const showMachine = MACHINE_METHODS.has(f.method) && machines.length > 0;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="payment-entry-dialog" className="bg-zinc-950 border border-zinc-800 text-zinc-100 max-w-md max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display uppercase">{entry ? "Editar pagamento" : "Adicionar pagamento"}</DialogTitle>
          <DialogDescription className="text-zinc-400 text-xs">Cada pagamento vira um lançamento no contas a receber.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label className="text-xs text-zinc-400">Forma</Label>
            <select data-testid="payment-entry-method" value={f.method} onChange={(e) => set("method", e.target.value)} className={sel}>{METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}</select></div>
          {showMachine && (
            <div className="col-span-2"><Label className="text-xs text-zinc-400">Maquininha</Label>
              <select data-testid="payment-entry-machine" value={f.machine_id} onChange={(e) => set("machine_id", e.target.value)} className={sel}>
                {machines.map((mm) => <option key={mm.id} value={mm.id}>{mm.name}{mm.is_default ? " (padrão)" : ""}</option>)}
              </select></div>
          )}
          <div><Label className="text-xs text-zinc-400">Valor (R$)</Label>
            <Input data-testid="payment-entry-amount" inputMode="decimal" value={f.amount} onChange={(e) => set("amount", e.target.value)} className="h-11 bg-zinc-950 border-zinc-800" /></div>
          <div><Label className="text-xs text-zinc-400">{split > 1 ? "1ª parcela em" : "Data"}</Label>
            <Input data-testid="payment-entry-date" type="date" value={f.date} onChange={(e) => set("date", e.target.value)} className="h-11 bg-zinc-950 border-zinc-800" /></div>
          {f.method === "credito" && <div><Label className="text-xs text-zinc-400">Parcelas no cartão</Label>
            <select data-testid="payment-entry-card" value={f.card} onChange={(e) => setF((p) => ({ ...p, card: e.target.value, split: entry ? p.split : e.target.value }))} className={sel}>{Array.from({ length: 12 }, (_, i) => <option key={i} value={String(i + 1)}>{i + 1}x</option>)}</select></div>}
          {!entry && <div><Label className="text-xs text-zinc-400">Dividir em</Label>
            <select data-testid="payment-entry-split" value={f.split} onChange={(e) => set("split", e.target.value)} className={sel}>{Array.from({ length: 24 }, (_, i) => <option key={i} value={String(i + 1)}>{i === 0 ? "1 lançamento" : `${i + 1} parcelas mensais`}</option>)}</select></div>}
          <div className="col-span-2"><Label className="text-xs text-zinc-400">Observação (opcional)</Label>
            <Input data-testid="payment-entry-note" value={f.note} onChange={(e) => set("note", e.target.value)} placeholder="ex.: entrada" className="h-11 bg-zinc-950 border-zinc-800" /></div>
          {(f.method === "credito" || f.method === "debito") && (
            <div className="col-span-2"><Label className="text-xs text-zinc-400">Código de autenticação (maquininha)</Label>
              <Input data-testid="payment-entry-auth" value={f.auth_code} onChange={(e) => set("auth_code", e.target.value)} placeholder="ex.: 123456" className="h-11 bg-zinc-950 border-zinc-800" /></div>
          )}
          {split === 1 && f.method !== "dinheiro" ? (
            <label className="col-span-2 flex items-center justify-between rounded-lg border border-zinc-800 px-3 py-2.5 text-sm">
              <span>Já recebido?</span><Switch data-testid="payment-entry-paid" checked={f.paid} onCheckedChange={(v) => set("paid", v)} />
            </label>
          ) : split > 1 ? <p data-testid="payment-entry-split-hint" className="col-span-2 text-xs text-zinc-400">{split}× de {brl(amount / split)} a cada 30 dias, todas como "a receber".</p> : null}
          {rate > 0 && (
            <div data-testid="payment-entry-fee" className="col-span-2 rounded-lg border border-sky-800/50 bg-sky-950/30 p-2 text-xs space-y-0.5">
              <div className="flex justify-between text-sky-200"><span>Taxa {m?.name ? `(${m.name}, ${rate.toFixed(2)}%)` : `(${rate}%)`}</span><span className="tabular-nums">− {brl(fee)}</span></div>
              <div className="flex justify-between text-zinc-100 font-bold"><span>Valor líquido</span><span data-testid="payment-entry-net" className="tabular-nums">{brl(amount - fee)}</span></div>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="bg-zinc-900 border-zinc-800">Cancelar</Button>
          <Button data-testid="payment-entry-save" disabled={saving || amount <= 0 || !f.date} onClick={submit} className="bg-green-brand hover:bg-green-brandDark text-white font-bold">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
